'use client';

import { useCallback } from 'react';
import { useAuthService } from './useAuthService';
import { LoginErrorCode, LOGIN_ERROR_MESSAGES } from '../../constants/auth';
import type { LoginResponse } from '../../infrastructure/auth/utils/types';

const ACCOUNT_LINK_ERROR_CODES = [LoginErrorCode.INVALID_EMAIL, LoginErrorCode.INCORRECT_PASSWORD];

type LoginParams = {
  email: string;
  password: string;
  remember?: boolean;
};

type LoginResult =
  | { success: true; response: LoginResponse }
  | { success: false; message: string; showAccountLinks: boolean };

type UseEmailLoginOptions = {
  onSuccess?: (response: LoginResponse) => Promise<void> | void;
  onError?: (message: string, showAccountLinks: boolean) => void;
};

export const useEmailLogin = (options?: UseEmailLoginOptions) => {
  const { useLogin } = useAuthService();
  const loginMutation = useLogin();

  const handleLoginResponse = useCallback(
    async (response: LoginResponse): Promise<LoginResult> => {
      const isLoginFailed = !response.success || !response.user;

      if (isLoginFailed) {
        const errorCode = response.error?.code;
        const message =
          (errorCode && LOGIN_ERROR_MESSAGES[errorCode]) ?? 'The password is incorrect. Please try again.';
        const shouldShowLinks = errorCode != null && ACCOUNT_LINK_ERROR_CODES.includes(errorCode);

        options?.onError?.(message, shouldShowLinks);
        return { success: false, message, showAccountLinks: shouldShowLinks };
      }

      await options?.onSuccess?.(response);
      return { success: true, response };
    },
    [options],
  );

  const login = useCallback(
    async (params: LoginParams): Promise<LoginResult> => {
      try {
        const response = await loginMutation.mutateAsync({
          email: params.email,
          password: params.password,
          remember: params.remember,
        });
        return handleLoginResponse(response);
      } catch {
        const message = 'Login failed. Please try again.';
        options?.onError?.(message, false);
        return { success: false, message, showAccountLinks: false };
      }
    },
    [loginMutation, handleLoginResponse, options],
  );

  return {
    login,
    isLoading: loginMutation.isPending,
  };
};
